import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Trophy, Star, Flame, Award, Wine, Crown, Gamepad2, ChevronRight, Loader2 } from "lucide-react";

const ICON = { wine: Wine, gamepad: Gamepad2, trophy: Trophy, flame: Flame, crown: Crown, star: Star };

export default function PlayRewards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/login?next=/play/rewards"); return; }
    api.get("/tlp/play/rewards").then((r) => setData(r.data)).catch(() => setData(null));
  }, [user]); // eslint-disable-line

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="play-rewards">
      <span className="chip-pink inline-flex items-center gap-1.5"><Trophy className="w-3 h-3" /> Play · Rewards</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Votre progression</h1>

      {!data && <div className="mt-12 flex items-center gap-2 text-[#8B95A1]"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>}

      {data && (
        <div className="mt-10 space-y-8">
          {/* Level + XP */}
          <div className="rounded-3xl bg-[#1F3A5F] text-white p-7" data-testid="rewards-level-card">
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.18em] text-[#FFD8E5] font-bold">Niveau</span>
              <div className="font-display text-4xl text-white">{data.level}</div>
              <span className="ml-auto text-2xl font-display text-[#FFD8E5]">{data.xp} <span className="text-sm">XP</span></span>
            </div>
            <div className="mt-4">
              <div className="h-3 rounded-full bg-white/15 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#E5276A] to-[#FFD8E5] transition-all" style={{ width: `${data.progress_pct}%` }} />
              </div>
              {data.next_level ? (
                <p className="mt-2 text-xs text-white/70">Encore {data.next_threshold - data.xp} XP pour devenir <b className="text-white">{data.next_level}</b></p>
              ) : (
                <p className="mt-2 text-xs text-white/70">Niveau max atteint — vous êtes une <b className="text-white">Légende</b> 🍷</p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid sm:grid-cols-4 gap-3" data-testid="rewards-stats">
            <Stat icon={Flame} label="Série actuelle" value={`${data.streak_days} j`} accent />
            <Stat icon={Wine} label="Dégustations" value={data.tasting_count} />
            <Stat icon={Gamepad2} label="Parties jouées" value={data.games_played} />
            <Stat icon={Trophy} label="Victoires" value={data.games_won} />
          </div>

          {/* Badges */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Badges débloqués</div>
            {data.badges.length === 0 ? (
              <div className="app-card p-6 text-center text-sm text-[#8B95A1]" data-testid="rewards-no-badges">
                Aucun badge encore — ajoutez une dégustation ou lancez une partie pour commencer.
              </div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3" data-testid="rewards-badges">
                {data.badges.map((b) => {
                  const Icon = ICON[b.icon] || Award;
                  return (
                    <div key={b.key} className="app-card p-4 flex items-center gap-3" data-testid={`rewards-badge-${b.key}`}>
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FCE4ED] to-[#E5276A]/20 flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6 text-[#E5276A]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-[#1F3A5F] text-sm">{b.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* CTAs */}
          <div className="grid sm:grid-cols-3 gap-3" data-testid="rewards-ctas">
            <Cta to="/carnet" icon={Wine} label="Ajouter une dégustation" hint="+5 XP" />
            <Cta to="/play/hosted" icon={Gamepad2} label="Lancer une partie" hint="+20 XP si victoire" />
            <Cta to="/learn/coach" icon={Star} label="Suivre mon plan coach" hint="Régularité = streak" />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`app-card p-4 ${accent ? "border-[#E5276A]" : ""}`}>
      <Icon className={`w-5 h-5 ${accent ? "text-[#E5276A]" : "text-[#1F3A5F]"}`} />
      <div className="font-display text-2xl text-[#1F3A5F] mt-2">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] mt-0.5">{label}</div>
    </div>
  );
}

function Cta({ to, icon: Icon, label, hint }) {
  return (
    <Link to={to} className="app-card p-4 hover:border-[#E5276A] flex items-center gap-3 group">
      <Icon className="w-5 h-5 text-[#E5276A] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#1F3A5F] text-sm">{label}</div>
        <div className="text-xs text-[#8B95A1]">{hint}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#8B95A1] group-hover:text-[#E5276A]" />
    </Link>
  );
}
